import { Slider, TextLink, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { isEmpty } from 'lodash';
import type { LngLatBoundsLike } from 'mapbox-gl';
import { useEffect } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { TOCChild, TOCItem, TOCParent } from '~/common/types';
import Icon from '~/components2/Icon';
import { useGeoreferencedMaps } from '~/hooks/useGeoreferencedMaps';
import { georeferencedMapLayerId } from '~/pages/projectDetails/map/georeferencedMapLayers.helpers';
import georeferencedMapsStore, {
  GEOREFERENCED_MAP_PAGES_STEP,
} from '~/pages/projectDetails/map/georeferencedMapsStore';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { GEOREFERENCED_MAP_MIN_ZOOM } from '~/store2/layerZoom';
import { PickerCheckbox } from './PickerCheckbox';
import { toPanelId } from './panelIds';
import { collectLeaves } from './sharedUtils';

function checkedState(leaves: readonly TOCChild[], visibleLayerIds: ReadonlySet<string>): boolean | 'indeterminate' {
  const visibleCount = leaves.filter((leaf) => visibleLayerIds.has(leaf.id)).length;
  if (visibleCount === 0) return false;
  return visibleCount === leaves.length ? true : 'indeterminate';
}

interface AnalyticsContext {
  project_id: string;
  organization_id?: string;
}

function toggleGeoreferencedMapLayer(leafId: string, visible: boolean, analytics: AnalyticsContext) {
  if (visible) layerStore.visibleLayerIds.add(leafId);
  else layerStore.visibleLayerIds.delete(leafId);
  captureTypedPosthogEvent(POSTHOG_EVENTS.georeferencedMapLayerToggled, {
    ...analytics,
    layer_id: leafId,
    action: visible ? 'on' : 'off',
  });
}

/** Parent checkbox: switch every page under a section, recorded as one section event. */
function toggleGeoreferencedMapSection(
  sectionId: string,
  leaves: readonly TOCChild[],
  visible: boolean,
  analytics: AnalyticsContext,
) {
  const childLayerIds = leaves.map((leaf) => leaf.id);
  for (const id of childLayerIds) {
    if (visible) layerStore.visibleLayerIds.add(id);
    else layerStore.visibleLayerIds.delete(id);
  }
  captureTypedPosthogEvent(POSTHOG_EVENTS.georeferencedMapLayerSectionToggled, {
    ...analytics,
    layer_section_id: sectionId,
    action: visible ? 'on' : 'off',
    child_layer_ids: childLayerIds,
  });
}

/** Moves focus to a page checkbox once it renders, so revealing pages never strands focus on a removed button. */
function focusPageCheckbox(leafId: string) {
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>(`[data-testid="toc-layer-toggle-${CSS.escape(leafId)}"]`)?.focus();
  });
}

/** Chevron: expand or collapse a section. */
export function setGeoreferencedMapSectionExpanded(
  sectionId: string,
  expanded: boolean,
  analytics: AnalyticsContext,
): void {
  if (expanded) layerStore.expandedSections.add(sectionId);
  else layerStore.expandedSections.delete(sectionId);
  captureTypedPosthogEvent(POSTHOG_EVENTS.georeferencedMapLayerSectionExpanded, {
    ...analytics,
    layer_section_id: sectionId,
    expanded,
  });
}

const seededSectionIds = new Set<string>();

/**
 * Expand each section the tree marks `expanded` the first time it appears this session, so a user
 * collapse survives the panel remounting. Direct set write (no analytics): a default, not a user gesture.
 */
export function seedGeoreferencedMapsDefaultExpansion(items: readonly TOCItem[]): void {
  for (const item of items) {
    if (!('children' in item)) continue;
    const sectionId = item.sectionId ?? item.label;
    if (item.expanded && !seededSectionIds.has(sectionId)) layerStore.expandedSections.add(sectionId);
    seededSectionIds.add(sectionId);
    seedGeoreferencedMapsDefaultExpansion(item.children);
  }
}

/** Test-only: re-arm the once-per-session default-expansion seed. */
export function resetGeoreferencedMapsDefaultExpansionSeed(): void {
  seededSectionIds.clear();
}

/** Search keeps an agency or file (with all its pages) when its own label matches. */
export function filterGeoreferencedMapsBySearch(items: readonly TOCParent[], searchTerm: string): TOCParent[] {
  if (isEmpty(searchTerm)) return [...items];
  const term = searchTerm.toLowerCase();
  return items.flatMap((agency) => {
    if (agency.label.toLowerCase().includes(term)) return [agency];
    const files = agency.children.filter((file) => file.label.toLowerCase().includes(term));
    return files.length > 0 ? [{ ...agency, children: files }] : [];
  });
}

/** The project's geo-referenced maps as agency → file → page checkboxes with zoom-to, under one opacity slider. */
export default function GeoreferencedMapsTOC({ searchTerm = '' }: { searchTerm?: string }) {
  const { georeferencedMapsTOCGroup } = useSnapshot(layerStore) as typeof layerStore;
  if (georeferencedMapsTOCGroup.length === 0) return null;
  return <GeoreferencedMapsTOCContent items={georeferencedMapsTOCGroup as TOCParent[]} searchTerm={searchTerm} />;
}

function GeoreferencedMapsTOCContent({ items, searchTerm }: { items: readonly TOCParent[]; searchTerm: string }) {
  const { visibleLayerIds, expandedSections } = useSnapshot(layerStore) as typeof layerStore;
  const { opacity, shownPageCounts } = useSnapshot(georeferencedMapsStore);
  const { current: map } = useMap();
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  // Same query GeoreferencedMapLayers runs, so this reads the cache; used for each page's footprint, PDF link and analytics ids.
  const { data: maps } = useGeoreferencedMaps(projectId);
  const mapsByLayerId = new Map((maps ?? []).map((m) => [georeferencedMapLayerId(m), m]));
  const { currentOrganizationId } = useSnapshot(rootStore);
  const analytics: AnalyticsContext = {
    project_id: projectId ?? '',
    organization_id: currentOrganizationId || undefined,
  };

  useEffect(() => {
    seedGeoreferencedMapsDefaultExpansion(items);
  }, [items]);

  const isSearching = !isEmpty(searchTerm);
  const rows = filterGeoreferencedMapsBySearch(items, searchTerm);
  if (rows.length === 0) return null;

  // A file's pages share its agency and source file, so its first page identifies it.
  const trackSourcePdfOpened = (leaves: readonly TOCChild[], { atPage = false } = {}) => {
    const page = mapsByLayerId.get(leaves[0]?.id ?? '');
    if (!projectId || !page) return;
    const event = atPage
      ? POSTHOG_EVENTS.georeferencedMapSourceDeepLinkPdfOpened
      : POSTHOG_EVENTS.georeferencedMapSourcePdfOpened;
    captureTypedPosthogEvent(event, {
      project_id: projectId,
      agency_id: page.agencyId,
      source_file_id: page.sourceFileId,
      data_request_id: page.dataRequestId,
      access: page.access,
    });
  };

  const zoomTo = (leafId: string) => {
    const footprint = mapsByLayerId.get(leafId)?.footprint;
    if (!map || !footprint) return;
    const ring = footprint.coordinates[0];
    const lngs = ring.map(([lng]) => lng);
    const lats = ring.map(([, lat]) => lat);
    const bounds: LngLatBoundsLike = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
    const camera = map.cameraForBounds(bounds, { padding: 40 });
    if (!camera) return;
    map.easeTo({ ...camera, zoom: Math.max(camera.zoom ?? 0, GEOREFERENCED_MAP_MIN_ZOOM), duration: 250 });
  };

  // `pageNumber` is 0-based in Grid Vision's manifest; PDF `#page=` is 1-based.
  const pdfPageHref = (leafId: string) => {
    const page = mapsByLayerId.get(leafId);
    return page?.sourceFileUrl ? `${page.sourceFileUrl}#page=${page.pageNumber + 1}` : undefined;
  };

  const renderLeaf = (leaf: TOCChild, fileLabel: string) => {
    const pageHref = pdfPageHref(leaf.id);
    return (
      <div
        key={leaf.id}
        data-testid={`toc-layer-${leaf.id}`}
        className="group relative my-1 ml-7 flex min-h-[1.75rem] flex-row items-center justify-between space-x-2"
      >
        <div className="grow">
          <PickerCheckbox
            data-testid={`toc-layer-toggle-${leaf.id}`}
            checked={visibleLayerIds.has(leaf.id)}
            onCheckedChange={(value) => {
              if (value === 'indeterminate') return;
              toggleGeoreferencedMapLayer(leaf.id, value, analytics);
            }}
            label={leaf.label}
          />
        </div>
        <Tooltip asChild text={`Zoom to ${fileLabel} ${leaf.label}`}>
          <button
            type="button"
            data-testid={`layer-zoom-${leaf.id}`}
            aria-label={`Zoom to ${fileLabel} ${leaf.label}`}
            onClick={() => zoomTo(leaf.id)}
            className="flex cursor-pointer items-center justify-center rounded p-1 hover:bg-grey-20 focus-ring"
          >
            <Icon icon="zoom" />
          </button>
        </Tooltip>
        {pageHref && (
          <Tooltip asChild text={`Open ${fileLabel} ${leaf.label} in a new tab`}>
            <a
              href={pageHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`layer-open-pdf-page-${leaf.id}`}
              aria-label={`Open ${fileLabel} ${leaf.label} in a new tab`}
              onClick={() => trackSourcePdfOpened([leaf], { atPage: true })}
              className="flex cursor-pointer items-center justify-center rounded p-1 hover:bg-grey-20 focus-ring"
            >
              <Icon icon="externalLink" />
            </a>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderParent = (
    parent: TOCParent,
    renderChildren: (child: TOCItem) => React.ReactNode,
    shownChildren: readonly TOCItem[] = parent.children,
    footer?: React.ReactNode,
    labelHref?: string,
  ) => {
    const sectionId = parent.sectionId ?? parent.label;
    const leaves = collectLeaves(parent.children);
    const isParentExpanded = expandedSections.has(sectionId) || isSearching;
    const parentPanelId = toPanelId('toc', sectionId);
    return (
      <div key={sectionId} data-testid={`toc-section-${sectionId}`} className="my-2 px-1">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            data-testid={`toc-section-expand-${sectionId}`}
            className="inline-flex appearance-none border-0 bg-transparent p-0 focus-ring"
            onClick={() => !isSearching && setGeoreferencedMapSectionExpanded(sectionId, !isParentExpanded, analytics)}
            aria-label={parent.label}
            aria-expanded={isParentExpanded}
            aria-controls={isParentExpanded ? parentPanelId : undefined}
          >
            <Icon icon={isParentExpanded ? 'chevronDown' : 'chevronRight'} />
          </button>
          <PickerCheckbox
            data-testid={`toc-section-toggle-${sectionId}`}
            checked={checkedState(leaves, visibleLayerIds)}
            onCheckedChange={(value) => {
              if (value === 'indeterminate') return;
              toggleGeoreferencedMapSection(sectionId, leaves, value, analytics);
            }}
            label={parent.label}
            labelSuffix={parent.labelSuffix}
            labelHref={labelHref}
            labelLinkTestId={labelHref && `toc-section-link-${sectionId}`}
            onLabelLinkClick={() => trackSourcePdfOpened(leaves)}
          />
        </div>
        {isParentExpanded && (
          <div id={parentPanelId} className="my-2 ml-2.5">
            {shownChildren.map(renderChildren)}
            {footer}
          </div>
        )}
      </div>
    );
  };

  const renderFile = (file: TOCItem) => {
    if (!('children' in file)) return null;
    const sectionId = file.sectionId ?? file.label;
    const shownCount = shownPageCounts[sectionId] ?? GEOREFERENCED_MAP_PAGES_STEP;
    const shownPages = file.children.slice(0, shownCount);
    const moreCount = Math.min(file.children.length - shownCount, GEOREFERENCED_MAP_PAGES_STEP);
    const moreText = `Show ${moreCount} more ${moreCount === 1 ? 'page' : 'pages'}`;
    const footer = moreCount > 0 && (
      <TextLink
        data-testid={`georeferenced-maps-show-more-${sectionId}`}
        aria-label={`${moreText} of ${file.label}`}
        className="my-1 ml-7"
        onClick={() => {
          georeferencedMapsStore.showMorePages(sectionId);
          const firstRevealed = file.children[shownCount];
          if (firstRevealed && 'id' in firstRevealed) focusPageCheckbox(firstRevealed.id);
        }}
      >
        {moreText}
      </TextLink>
    );
    // Read from the query, not the tree, so the signed URL renews on refetch.
    const fileHref = mapsByLayerId.get(collectLeaves(file.children)[0]?.id ?? '')?.sourceFileUrl ?? undefined;
    return renderParent(
      file,
      (page) => !('children' in page) && renderLeaf(page, file.label),
      shownPages,
      footer,
      fileHref,
    );
  };

  return (
    <div className="my-1 px-1">
      <div className="my-2 px-1">
        <Slider
          data-testid="georeferenced-maps-opacity-slider"
          label="Opacity"
          min={0}
          max={100}
          step={5}
          value={Math.round(opacity * 100)}
          onValueChange={(value) => georeferencedMapsStore.setOpacity(value / 100)}
          formatValue={(value) => `${value}%`}
        />
      </div>
      {rows.map((agency) => renderParent(agency, renderFile))}
    </div>
  );
}
